import {
  ChangeDetectionStrategy,
  Component,
  computed,
  HostListener,
  input,
  output,
  signal,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { Avatar } from 'primeng/avatar';
import { Tooltip } from 'primeng/tooltip';
import { CapitalizeFirstPipe } from '@brighthub/helpers';
import {
  EnrichedQueueRow,
  HandoffState,
  InteractionChannel,
  PresenceState,
  QueueRowAction,
  QueueRowActionEvent,
  UrgencyBadge,
} from './client-queue.model';
import { QualityRisk } from '../client-quality/client-quality.model';

const CHANNEL_ICONS: Record<InteractionChannel, string> = {
  email: 'pi pi-envelope',
  sms:   'pi pi-comment',
  call:  'pi pi-phone',
  chat:  'pi pi-comments',
};

const CHANNEL_LABELS: Record<InteractionChannel, string> = {
  email: 'Email',
  sms:   'SMS',
  call:  'Call',
  chat:  'Chat',
};

const URGENCY_LABELS: Record<UrgencyBadge, string> = {
  overdue:          'Overdue',
  due_soon:         'Due soon',
  on_track:         'On track',
  waiting_on_client: 'Waiting on client',
};

const URGENCY_CLASSES: Record<UrgencyBadge, string> = {
  overdue:           'bai-qrow__badge--danger',
  due_soon:          'bai-qrow__badge--warn',
  on_track:          'bai-qrow__badge--success',
  waiting_on_client: 'bai-qrow__badge--neutral',
};

const HANDOFF_LABELS: Record<NonNullable<HandoffState>, string> = {
  ai_in_progress: 'AI in progress',
  needs_fa:       'Needs FA',
  escalated:      'Escalated',
};

const HANDOFF_CLASSES: Record<NonNullable<HandoffState>, string> = {
  ai_in_progress: 'bai-qrow__badge--indigo',
  needs_fa:       'bai-qrow__badge--amber',
  escalated:      'bai-qrow__badge--danger',
};

@Component({
  selector: 'bai-client-queue-row',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, Avatar, Tooltip, CapitalizeFirstPipe],
  templateUrl: './client-queue-row.component.html',
  styleUrl: './client-queue-row.component.scss',
})
export class ClientQueueRowComponent {
  // ---------------------------------------------------------------------------
  // Inputs / outputs
  // ---------------------------------------------------------------------------

  readonly row = input.required<EnrichedQueueRow>();
  readonly selected = input(false);
  readonly collapsed = input(false);
  /** Optional quality risk badge derived from the Quality tab state. */
  readonly qualityRisk = input<QualityRisk>('none');

  readonly select = output<EnrichedQueueRow>();
  readonly rowAction = output<QueueRowActionEvent>();

  // ---------------------------------------------------------------------------
  // Quick-action menu state
  // ---------------------------------------------------------------------------

  readonly menuOpen = signal(false);

  toggleMenu(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.menuOpen.update((v) => !v);
  }

  closeMenu(): void {
    this.menuOpen.set(false);
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.menuOpen.set(false);
  }

  // ---------------------------------------------------------------------------
  // Computed display values
  // ---------------------------------------------------------------------------

  readonly initials = computed(() => {
    const { firstName, lastName } = this.row().session.subjectData;
    return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase();
  });

  readonly presenceClass = computed((): string => {
    const map: Record<PresenceState, string> = {
      active:  'bai-qrow__presence--active',
      recent:  'bai-qrow__presence--recent',
      offline: 'bai-qrow__presence--offline',
    };
    return map[this.row().presence];
  });

  readonly presenceLabel = computed((): string => {
    const r = this.row();
    if (r.presence === 'active') return 'Active now';
    if (r.presence === 'recent') return `Active ${r.lastActiveMinutesAgo}m ago`;
    return 'Offline';
  });

  readonly channelIcon = computed(() => CHANNEL_ICONS[this.row().lastChannel]);
  readonly channelLabel = computed(() => CHANNEL_LABELS[this.row().lastChannel]);

  readonly urgencyLabel = computed(() => URGENCY_LABELS[this.row().urgency]);
  readonly urgencyClass = computed(() => URGENCY_CLASSES[this.row().urgency]);

  readonly handoffLabel = computed((): string | null => {
    const h = this.row().handoff;
    return h ? HANDOFF_LABELS[h] : null;
  });

  readonly handoffClass = computed((): string | null => {
    const h = this.row().handoff;
    return h ? HANDOFF_CLASSES[h] : null;
  });

  readonly displayedWaitMinutes = computed((): number =>
    Math.min(this.row().waitingMinutes, 5)
  );

  readonly waitLabel = computed((): string => {
    const m = this.displayedWaitMinutes();
    if (m < 1) return 'Just now';
    return `Waiting ${m}m`;
  });

  readonly rankTooltip = computed((): string => {
    const r = this.row();
    const reasons = r.rankReasons;
    const bucketLabel: Record<string, string> = {
      now:   'Why Now:',
      next:  'Why Next:',
      later: 'Why Later:',
    };
    const heading = bucketLabel[r.bucket] ?? 'Why here:';
    if (reasons.length === 0) return `${heading}\nNo urgent signals`;
    return heading + '\n' + reasons.map((reason) => `• ${this.normalizeReasonLabel(reason.label)}`).join('\n');
  });

  readonly avatarTooltip = computed(() => {
    const s = this.row().session.subjectData;
    return `${s.firstName} ${s.lastName}\n${s.company}`;
  });

  readonly bucketClass = computed(() => `bai-qrow--bucket-${this.row().bucket}`);

  readonly qualityRiskClass = computed((): string | null => {
    const risk = this.qualityRisk();
    if (risk === 'none') return null;
    return `bai-qrow__quality-badge--${risk}`;
  });

  readonly qualityRiskLabel = computed((): string => {
    const map: Record<QualityRisk, string> = {
      good:    'Good',
      watch:   'Watch',
      at_risk: 'At Risk',
      none:    '',
    };
    return map[this.qualityRisk()];
  });

  readonly qualityRiskTooltip = computed((): string => {
    const map: Record<QualityRisk, string> = {
      good:    'Quality: Good — NPS and EARN signals are positive',
      watch:   'Quality: Watch — some quality signals need attention',
      at_risk: 'Quality: At Risk — quality signals indicate risk',
      none:    '',
    };
    return map[this.qualityRisk()];
  });

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  onSelect(): void {
    this.select.emit(this.row());
  }

  onAction(action: QueueRowAction, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.menuOpen.set(false);
    this.rowAction.emit({ action, row: this.row() });
  }

  private normalizeReasonLabel(label: string): string {
    if (/^Waiting\s+\d+\+?\s*min$/i.test(label)) return 'Waiting 5m';
    return label;
  }
}
